import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ReportInProgressAgentsCompanies } from '@core/models/statistics-report.model';

import {
  ApexResponsive,
  ApexChart,
  ApexTheme,
  ApexYAxis,
  ApexLegend,
  ApexPlotOptions,
  ChartComponent,
  ApexTooltip,
  ApexAxisChartSeries,
  ApexXAxis,
  ApexGrid,
} from 'ng-apexcharts';
import { CapitalizeFirstLetterPipe } from '@shared/pipes/capitalize-first-letter.pipe';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  plotOptions?: ApexPlotOptions;
  xaxis?: ApexXAxis;
  yaxis?: ApexYAxis;
  legend?: ApexLegend;
  tooltip?: ApexTooltip;
  theme?: ApexTheme;
  dataLabels?: any;
  responsive?: ApexResponsive[];
  grid?: ApexGrid;
};

@Component({
  selector: 'app-company-in-progress-agents-statistics',
  templateUrl: './company-in-progress-agents-statistics.component.html',
  styleUrls: ['./company-in-progress-agents-statistics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CapitalizeFirstLetterPipe],
})
export class CompanyInProgressAgentsStatisticsComponent implements OnChanges {
  @Input() statistics: ReportInProgressAgentsCompanies[] = [];
  @ViewChild('chart') chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;

  constructor(
    private readonly capitalizeFirstLetter: CapitalizeFirstLetterPipe,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.statistics) {
      this.refreshChart();
    }
  }

  private refreshChart(): void {
    const sortedStats = [...this.statistics].sort(
      (a, b) => b.companyCount - a.companyCount,
    );

    const step = 1;
    const maxValue = Math.max(...sortedStats.map((s) => s.companyCount));
    const maxRounded = Math.ceil(maxValue / step) * step;

    this.chartOptions = {
      series: [
        {
          name: 'Company Progress',
          data: sortedStats.map((s) => s.companyCount),
        },
      ],

      chart: {
        type: 'bar',
        height: 350,
        toolbar: {
          show: false,
        },
      },

      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '60%',
          dataLabels: {
            position: 'center',
          },
        },
      },

      dataLabels: {
        enabled: true,
        formatter: (val) => `${val}`,
        textAnchor: 'middle',
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#fff'],
        },
      },

      grid: {
        show: false,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: false,
          },
        },
      },

      xaxis: {
        min: 0,
        max: maxRounded,
        tickAmount: maxRounded / step,
        categories: sortedStats.map((s) =>
          this.capitalizeFirstLetter.transform(s.fullName),
        ),
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },

      yaxis: {
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },

      tooltip: {
        y: {
          formatter: (val) => `${val}`,
        },
      },

      legend: {
        show: false,
      },

      theme: {
        monochrome: {
          enabled: true,
          shadeTo: 'dark',
          shadeIntensity: 0.8,
        },
      },
    };
  }
}
